import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type HypothesisStatus = "pending" | "validated" | "invalidated" | "in_progress";

export type HypothesisCategory = "market" | "technical" | "financial" | "organizational" | "strategic" | "general";

export interface Hypothesis {
  id: string;
  session_id: string;
  workspace_id: string;
  hypothesis: string;
  source: string;
  status: HypothesisStatus;
  validation_notes: string | null;
  evidence: string | null;
  validated_at: string | null;
  validation_method: string | null;
  validation_deadline: string | null;
  confidence_level: number;
  impact_if_wrong: string | null;
  category: HypothesisCategory;
  created_at: string;
  updated_at: string;
  created_by: string;
  // Joined data
  session?: {
    summary: string | null;
    decision_type: string | null;
  };
}

interface CreateHypothesisInput {
  session_id: string;
  workspace_id: string;
  hypothesis: string;
  source?: string;
  created_by: string;
}

interface UpdateHypothesisInput {
  id: string;
  status?: HypothesisStatus;
  validation_notes?: string;
  evidence?: string;
  validation_method?: string;
  validation_deadline?: string;
  confidence_level?: number;
  impact_if_wrong?: string;
  category?: HypothesisCategory;
}

export function useHypotheses(workspaceId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["hypotheses", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];

      const { data, error } = await supabase
        .from("session_hypotheses")
        .select(`
          *,
          session:decision_sessions(summary, decision_type)
        `)
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Hypothesis[];
    },
    enabled: !!workspaceId,
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateHypothesisInput) => {
      const { data, error } = await supabase
        .from("session_hypotheses")
        .insert(input)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hypotheses", workspaceId] });
      toast({
        title: "Hypothese hinzugefügt",
        description: "Die Hypothese wurde erfolgreich zum Tracking hinzugefügt.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateHypothesisInput) => {
      const { id, ...updates } = input;
      const updateData: Record<string, unknown> = { ...updates };
      
      if (updates.status === "validated" || updates.status === "invalidated") {
        updateData.validated_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from("session_hypotheses")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hypotheses", workspaceId] });
      toast({
        title: "Hypothese aktualisiert",
        description: "Der Status wurde erfolgreich geändert.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("session_hypotheses")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hypotheses", workspaceId] });
      toast({
        title: "Hypothese entfernt",
        description: "Die Hypothese wurde aus dem Tracking entfernt.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const importFromReport = async (
    sessionId: string,
    workspaceId: string,
    hypotheses: string[],
    userId: string
  ) => {
    const inserts = hypotheses.map((hypothesis) => ({
      session_id: sessionId,
      workspace_id: workspaceId,
      hypothesis,
      source: "report",
      created_by: userId,
    }));

    const { error } = await supabase
      .from("session_hypotheses")
      .insert(inserts);

    if (error) throw error;
    
    queryClient.invalidateQueries({ queryKey: ["hypotheses", workspaceId] });
    toast({
      title: "Hypothesen importiert",
      description: `${hypotheses.length} Hypothesen wurden zum Tracking hinzugefügt.`,
    });
  };

  return {
    hypotheses: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    createHypothesis: createMutation.mutate,
    updateHypothesis: updateMutation.mutate,
    deleteHypothesis: deleteMutation.mutate,
    importFromReport,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}
